import * as React from "react";
import FileIcons from "../../utils/seti-ui-icons.json";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { FileData } from "@/utils/types";
import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  file: FileData;
  level: number;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
  selected: boolean;
  expanded: boolean;
  onEditSubmit: (oldPath: string, newPath: string) => void;
}

const FileItem: React.FC<Props> = React.memo((props) => {
  const paddingLeft = 20 * props.level;

  const filename = props.file.fullPath.split("/").pop();
  const ext = filename?.split(".").pop();

  // @ts-expect-error TS2538
  const fileName = FileIcons[filename];
  // @ts-expect-error TS2538
  const iconForExt = FileIcons[ext];

  const iconToUse =
    fileName || iconForExt || "/icons/file-extension-items/seti-ui/default.svg";

  const [isEditing, setIsEditing] = useState(false);
  const [newFileName, setNewFileName] = useState(filename);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle the blur event or Enter submission while editing
  const handleInputBlurOrSubmit = () => {
    setIsEditing(false);
    // @ts-expect-error TS18048
    if (newFileName.trim() && newFileName !== filename) {
      // @ts-expect-error TS18048
      props.onEditSubmit(props.file.fullPath, newFileName.trim());
    } else {
      setNewFileName(filename); // Revert to original name if empty or canceled
    }
  };

  useEffect(() => {
    // Only focus if explicitly entering editing mode
    if (isEditing) {
      const inputEl = inputRef.current;
      if (inputEl) {
        inputEl.focus();
        inputEl.select(); // Optionally select all text for easy renaming
      }
    }
  }, [isEditing]);

  // Handle Enter to start editing, Escape to cancel editing
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!props.selected) return;

      if (e.key === "Enter") {
        setIsEditing(true);
      } else if (e.key === "Escape") {
        setIsEditing(false);
      }
    },
    [props.selected]
  );

  return (
    <>
      <div
        className={`file-item ${props.selected ? "selected" : ""} py-0.5`}
        style={{ paddingLeft: `${paddingLeft + 4}px` }}
        onClick={(e) => props.onClick(e)}
        onDoubleClick={props.onDoubleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span>
          {props.file.items ? (
            props.expanded ? (
              <ExpandMore sx={{ width: 14, height: 14 }} />
            ) : (
              <ChevronRightIcon sx={{ width: 14, height: 14 }} />
            )
          ) : (
            <img
              alt={ext}
              src={iconToUse}
              width="20"
              height="20"
              style={{
                boxSizing: "initial",
              }}
              className="max-w-none"
            />
          )}
        </span>
        <span>
          {isEditing ? (
            <input
              ref={inputRef}
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              onBlur={(event) => {
                if (event.relatedTarget !== null) return;

                handleInputBlurOrSubmit();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleInputBlurOrSubmit();
                else if (e.key === "Escape") {
                  setIsEditing(false);
                  setNewFileName(filename);
                }
              }}
              className="w-full rounded-none focus:outline-none bg-background"
            />
          ) : (
            filename
          )}
        </span>
      </div>
    </>
  );
});

export default FileItem;
